import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Lock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PinGatePage() {
  const navigate = useNavigate();
  const { loading: themeLoading } = useTheme();
  const { user } = useAuth();
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const inputRefs = useRef([]);

  // Neutralize branding on PIN page
  useEffect(() => {
    document.title = 'Entrez votre Code PIN';
    
    // Clear OG tags once
    ['og:title', 'og:description', 'og:image'].forEach(prop => {
      const el = document.querySelector(`meta[property="${prop}"]`);
      if (el) el.setAttribute('content', '');
    });
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', '');
    
    // Hide favicon
    const favicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]');
    favicons.forEach(f => f.setAttribute('data-href-backup', f.href));
    favicons.forEach(f => f.href = 'data:,');
    const splash = document.getElementById('splash-screen');
    if (splash) splash.style.display = 'none';

    return () => {
      // ONLY restore branding if PIN was verified (not during StrictMode double-mount)
      const pinVerified = sessionStorage.getItem('pin_verified') === 'true';
      if (!pinVerified) return;
      
      // Defer branding restoration to not block navigation
      setTimeout(() => {
        const cached = localStorage.getItem('pb_branding_cache');
        if (cached) {
          try {
            const branding = JSON.parse(cached);
            if (branding.platformName) document.title = branding.platformName;
            if (branding.faviconUrl) {
              let link = document.querySelector("link[rel~='icon']");
              if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
              link.href = branding.faviconUrl;
            }
            const mOgT = document.querySelector('meta[property="og:title"]');
            const mOgD = document.querySelector('meta[property="og:description"]');
            const mD = document.querySelector('meta[name="description"]');
            if (mOgT) mOgT.setAttribute('content', branding.ogTitle || branding.platformName || '');
            if (mOgD) mOgD.setAttribute('content', branding.ogDescription || '');
            if (mD) mD.setAttribute('content', branding.ogDescription || '');
          } catch(e) {}
        }
        favicons.forEach(f => {
          const backup = f.getAttribute('data-href-backup');
          if (backup) f.href = backup;
        });
        if (!document.querySelector('link[rel="manifest"]')) {
          const m = document.createElement('link'); m.rel = 'manifest'; m.href = '/api/manifest.json'; document.head.appendChild(m);
        }
        if (!document.querySelector('link[rel="apple-touch-icon"]')) {
          const a1 = document.createElement('link'); a1.rel = 'apple-touch-icon'; a1.sizes = '180x180'; a1.href = '/api/pwa-icon-180.png'; document.head.appendChild(a1);
          const a2 = document.createElement('link'); a2.rel = 'apple-touch-icon'; a2.sizes = '192x192'; a2.href = '/api/pwa-icon-192.png'; document.head.appendChild(a2);
        }
        if (!document.querySelector('meta[name="apple-mobile-web-app-capable"]')) {
          const mc = document.createElement('meta'); mc.name = 'apple-mobile-web-app-capable'; mc.content = 'yes'; document.head.appendChild(mc);
          const mc2 = document.createElement('meta'); mc2.name = 'mobile-web-app-capable'; mc2.content = 'yes'; document.head.appendChild(mc2);
        }
      }, 0);
    };
  }, []);

  const getHomeRoute = () => {
    if (!user?.role) return '/login';
    if (user.role === 'client') return '/client/dashboard';
    if (user.role === 'analyst') return '/analyst/dashboard';
    if (user.role === 'super_admin' || user.role === 'admin_ops' || user.role === 'bureau') return '/admin/dashboard';
    return '/login';
  };

  // Check if PIN gate is enabled and if already verified
  useEffect(() => {
    const checkPinSettings = async () => {
      // First check if already verified in this session
      const pinVerified = sessionStorage.getItem('pin_verified');
      if (pinVerified === 'true') {
        navigate(getHomeRoute());
        return;
      }

      // Check if PIN gate is enabled
      try {
        const response = await axios.get(`${API}/api/settings/platform`);
        const settings = response.data;
        if (!settings.gate_pin_enabled) {
          // PIN gate disabled, go directly to login
          sessionStorage.setItem('pin_verified', 'true');
          navigate(getHomeRoute());
          return;
        }
      } catch (error) {
        console.error('Failed to check settings:', error);
      }
      setCheckingSettings(false);
    };
    
    checkPinSettings();
  }, [navigate, user]);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        verifyPin(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newPin = pastedData.split('');
      setPin(newPin);
      verifyPin(pastedData);
    }
  };

  const verifyPin = async (pinCode) => {
    setIsVerifying(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/auth/verify-gate-pin`, { pin: pinCode });
      if (response.data.valid) {
        sessionStorage.setItem('pin_verified', 'true');
        navigate(getHomeRoute());
        return; // Skip finally's setIsVerifying — we're leaving
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Code PIN invalide';
      setError(detail);
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setIsVerifying(false);
  };

  // Show minimal loading while checking settings
  if (themeLoading || checkingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0f1a' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'hsl(var(--background))' }}
    >
      <div className="w-full max-w-md">
        {/* PIN Input Card - No logo or title */}
        <div 
          className="rounded-xl p-8"
          style={{ 
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))'
          }}
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <p className="text-muted-foreground">
              Entrez votre code PIN pour accéder à la plateforme
            </p>
          </div>

          <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                autoFocus={index === 0}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isVerifying}
                className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary"
                style={{
                  background: 'hsl(var(--background))',
                  borderColor: error ? 'hsl(var(--destructive))' : 'hsl(var(--border))',
                  color: 'hsl(var(--foreground))'
                }}
                data-testid={`pin-input-${index}`}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center justify-center gap-2 text-destructive mb-4">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Loading indicator */}
          {isVerifying && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center mt-4">
            Entrez le code PIN à 6 chiffres
          </p>
        </div>
      </div>
    </div>
  );
}
